import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Alerte, Bouton, Carte, Champ, ChampMotDePasse } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { ApercuArrivee } from './ApercuArrivee';

/** Ce que l'API rend en échange du jeton de l'e-mail. */
interface ContextePreAuth {
  preAuthToken: string;
  email: string;
  inviterName: string;
  hasAccount: boolean;
}

/**
 * Arrivée du parent invité.
 *
 * Le moment le plus délicat du produit : ce parent n'a rien demandé et peut être hostile
 * à l'outil. L'écran présente donc la symétrie des droits avant tout, et le refus comme
 * une issue prévue — jamais comme un échec (docs/02 §1.2 et §1.5).
 *
 * **L'écran ne redemande jamais l'adresse.** Le lien cliqué l'a déjà prouvée ; l'API la
 * rend avec le contexte, et l'écran ne réclame que ce qu'il ignore — un prénom et un mot
 * de passe, ou le seul mot de passe si le compte existe déjà. Le parcours d'origine
 * renvoyait vers la page de connexion : créer un compte, ressaisir la même adresse,
 * attendre un second e-mail de confirmation, revenir. Quatre pas de plus au moment précis
 * où l'on abandonne.
 *
 * Le parent déjà connecté ne voit rien de tout cela : il n'a qu'à accepter ou décliner.
 *
 * Dès l'acceptation, le calendrier déjà rempli s'affiche : la valeur est visible avant
 * qu'on demande quoi que ce soit.
 */
export function Rejoindre({ jeton, surArrivee }: { jeton: string; surArrivee?: (id: string) => void }) {
  const { client, profil, chargement, rafraichirProfil } = useSession();
  const queryClient = useQueryClient();
  const [dossierId, setDossierId] = useState<string | null>(null);
  const [decline, setDecline] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [arriveeADiriger, setArriveeADiriger] = useState<string | null>(null);
  const dejaDirige = useRef(false);

  /**
   * Navigation différée, après que la session est **visible du routeur**.
   *
   * Les gardes de route lisent la session dans le contexte du routeur, et ce contexte ne
   * change qu'au rendu suivant. Naviguer dans la foulée de la création du compte faisait
   * donc évaluer la garde du dossier avec l'ancien contexte — sans profil — et le parent
   * qui venait de créer son compte atterrissait sur l'écran de connexion, alors même que
   * l'en-tête affichait déjà son adresse. Symptôme constaté au navigateur, pas en test :
   * les tests montent l'écran sans routeur, donc sans garde à traverser.
   *
   * Attendre `profil` suffit : quand il est posé, le rendu qui porte le nouveau contexte a
   * déjà eu lieu. Le drapeau évite qu'un `surArrivee` recréé à chaque rendu ne relance la
   * navigation en boucle.
   */
  useEffect(() => {
    if (!arriveeADiriger || !profil || dejaDirige.current) return;
    dejaDirige.current = true;
    surArrivee?.(arriveeADiriger);
  }, [arriveeADiriger, profil, surArrivee]);

  // Le contexte n'est demandé que pour un visiteur sans session : le parent déjà connecté
  // n'a besoin d'aucun compte, et ouvrir un jeton de pré-authentification pour lui serait
  // en créer un dont personne ne se servirait.
  const anonyme = !chargement && !profil;
  const contexte = useQuery({
    queryKey: ['pre-auth', jeton],
    enabled: anonyme && jeton !== '',
    retry: false,
    // Le jeton de pré-authentification ne vaut qu'une fois : le redemander à chaque montage
    // laisserait derrière lui une traînée de jetons inutilisés.
    staleTime: Infinity,
    queryFn: () =>
      client.request<ContextePreAuth>('/pre-auth/invitation', {
        method: 'POST',
        body: { token: jeton },
      }),
  });

  const accepter = useMutation({
    mutationFn: () =>
      client.request<{ caseFileId: string }>('/invitations/accept', {
        method: 'POST',
        body: { token: jeton },
      }),
    onSuccess: (resultat) => {
      setDossierId(resultat.caseFileId);
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
      surArrivee?.(resultat.caseFileId);
    },
  });

  const rejoindreAvecCompte = useMutation({
    mutationFn: async () => {
      const preAuthToken = contexte.data?.preAuthToken;
      if (!preAuthToken) throw new Error('Contexte d’invitation indisponible.');
      return client.completerPreAuth({
        preAuthToken,
        password: motDePasse,
        ...(contexte.data?.hasAccount ? {} : { displayName: prenom }),
      });
    },
    onSuccess: async (resultat) => {
      // L'écran d'arrivée est posé tout de suite — le parent voit qu'il a rejoint — puis le
      // profil est rechargé. La navigation, elle, attend l'effet ci-dessus.
      setDossierId(resultat.caseFileId);
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
      await rafraichirProfil();
      setArriveeADiriger(resultat.caseFileId);
    },
  });

  const decliner = useMutation({
    // Deux routes pour un même geste, parce que l'une enregistre un auteur et l'autre non :
    // le parent déjà connecté signe son refus, le visiteur sans compte ne le peut pas — et
    // ne doit surtout pas avoir à s'inscrire pour dire non.
    mutationFn: () =>
      anonyme
        ? client.request('/pre-auth/decline', { method: 'POST', body: { token: jeton } })
        : client.request('/invitations/decline', { method: 'POST', body: { token: jeton } }),
    onSuccess: () => setDecline(true),
  });

  if (decline) {
    return (
      <>
        <h1>C’est noté</h1>
        <Carte>
          <p style={{ margin: 0 }}>
            Vous n'avez pas rejoint ce dossier. Rien d'autre ne se passera, et personne ne
            vous relancera. Vous pourrez toujours rejoindre plus tard si vous changez d'avis.
          </p>
        </Carte>
      </>
    );
  }

  if (dossierId) {
    return (
      <>
        <h1>Vous avez rejoint le dossier</h1>
        <Carte titre="Le calendrier de garde">
          <ApercuArrivee caseFileId={dossierId} />
        </Carte>
      </>
    );
  }

  const compteExistant = contexte.data?.hasAccount ?? false;
  const formulaireIncomplet =
    motDePasse.length === 0 || (!compteExistant && prenom.trim().length === 0);

  return (
    <>
      <h1>Rejoindre un dossier</h1>
      <Carte>
        <p style={{ marginTop: 0 }}>
          {contexte.data
            ? `${contexte.data.inviterName} utilise Repère pour organiser le quotidien de vos enfants et vous propose d'y accéder.`
            : "L'autre parent de vos enfants utilise Repère pour organiser le quotidien et vous propose d'y accéder."}
        </p>
        <p>
          <strong>Vous y aurez exactement les mêmes droits.</strong> L'historique
          n'appartient à aucun des deux : personne ne peut en retirer quoi que ce soit,
          ni vous exclure du dossier.
        </p>

        {contexte.isError ? (
          <Alerte titre="Ce lien n’est plus valable">
            {messageDErreur(contexte.error)} Demandez à l'autre parent de vous envoyer une
            nouvelle invitation.
          </Alerte>
        ) : null}
        {accepter.isError ? (
          <Alerte titre="L’invitation n’a pas pu être acceptée">
            {messageDErreur(accepter.error)}
          </Alerte>
        ) : null}
        {rejoindreAvecCompte.isError ? (
          <Alerte titre="Vous n’avez pas pu rejoindre le dossier">
            {messageDErreur(rejoindreAvecCompte.error)}
          </Alerte>
        ) : null}
        {decliner.isError ? (
          <Alerte titre="La réponse n’a pas pu être enregistrée">
            {messageDErreur(decliner.error)}
          </Alerte>
        ) : null}

        {anonyme && contexte.data ? (
          <>
            {/* L'adresse est montrée, jamais proposée à la saisie : c'est celle qui a reçu
                le lien, et la modifier annulerait la preuve qui dispense de confirmation. */}
            <p className="discret">
              Votre compte sera rattaché à <strong>{contexte.data.email}</strong>, l'adresse
              qui a reçu cette invitation.
            </p>
            {compteExistant ? (
              <p>Vous avez déjà un compte : votre mot de passe suffit.</p>
            ) : (
              <Champ
                label="Votre prénom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                aide="C'est le nom sous lequel l'autre parent vous verra dans le dossier."
              />
            )}
            <ChampMotDePasse
              label={compteExistant ? 'Votre mot de passe' : 'Choisissez un mot de passe'}
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              autoComplete={compteExistant ? 'current-password' : 'new-password'}
              aide={compteExistant ? undefined : 'Au moins 12 caractères.'}
            />
            <Bouton
              onClick={() => rejoindreAvecCompte.mutate()}
              disabled={formulaireIncomplet || rejoindreAvecCompte.isPending}
            >
              {rejoindreAvecCompte.isPending ? 'En cours…' : 'Rejoindre le dossier'}
            </Bouton>{' '}
          </>
        ) : null}

        {!anonyme ? (
          <>
            <Bouton onClick={() => accepter.mutate()} disabled={accepter.isPending}>
              {accepter.isPending ? 'En cours…' : 'Rejoindre le dossier'}
            </Bouton>{' '}
          </>
        ) : null}

        <Bouton
          variante="secondaire"
          onClick={() => decliner.mutate()}
          disabled={decliner.isPending}
        >
          Ne pas rejoindre
        </Bouton>
      </Carte>
    </>
  );
}
